import { useState } from "react";
import { Link } from "react-router-dom";
import { Logo } from "@/components/brand/Logo";
import heroImage from "@/assets/home/hero-editorial.jpg";

const kicker = "uppercase tracking-[0.28em] text-[11px] text-[hsl(var(--ink-muted))]";
const rule = "h-px w-full bg-[hsl(var(--ink))]/25";

const navLinks = [
  { to: "/preview/first-analysis", label: "Visibility Snapshot" },
  { to: "/preview/weekly-issue", label: "How it works" },
  { to: "/faq", label: "FAQ" },
  { to: "/sign-in", label: "Sign in" },
];

const Index = () => {
  const year = new Date().getFullYear();
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <div className="min-h-screen bg-[hsl(var(--background))] text-[hsl(var(--ink))] font-sans">
      {/* Masthead */}
      <header className="px-8 md:px-14 pt-8 md:pt-10">
        <div className="flex items-center justify-between gap-8">
          <Link to="/" aria-label="Retail Ranker home" className="flex items-center gap-3">
            <Logo variant="mark" eager className="h-16 w-16 md:h-20 md:w-20 object-contain -ml-1" />
            <span className="font-serif text-2xl md:text-[26px] leading-none">
              Retail <span className="italic" style={{ color: "hsl(var(--plum))" }}>Ranker</span>
            </span>
          </Link>
          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-8 text-[12px] font-medium tracking-[0.18em] uppercase text-[hsl(var(--ink-muted))]">
            {navLinks.map((l) => (
              <Link key={l.to} to={l.to} className="hover:text-[hsl(var(--ink))]">{l.label}</Link>
            ))}
          </nav>
          {/* Mobile menu button */}
          <button
            className="md:hidden flex flex-col justify-center items-center gap-[5px] w-10 h-10"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((s) => !s)}
          >
            <span className={`block h-px w-6 bg-[hsl(var(--ink))] transition-transform duration-300 ${menuOpen ? "rotate-45 translate-y-[6px]" : ""}`} />
            <span className={`block h-px w-6 bg-[hsl(var(--ink))] transition-opacity duration-300 ${menuOpen ? "opacity-0" : ""}`} />
            <span className={`block h-px w-6 bg-[hsl(var(--ink))] transition-transform duration-300 ${menuOpen ? "-rotate-45 -translate-y-[6px]" : ""}`} />
          </button>
        </div>
        {/* Mobile nav panel */}
        <div className={`md:hidden overflow-hidden transition-all duration-300 ${menuOpen ? "max-h-60 mt-4" : "max-h-0"}`}>
          <nav className="flex flex-col gap-3 text-[12px] tracking-[0.18em] uppercase text-[hsl(var(--ink-muted))]">
            {navLinks.map((l) => (
              <Link key={l.to} to={l.to} className="hover:text-[hsl(var(--ink))] py-1" onClick={() => setMenuOpen(false)}>{l.label}</Link>
            ))}
          </nav>
        </div>
        <div className="mt-6 flex items-center justify-center text-[10px] tracking-[0.3em] uppercase text-[hsl(var(--ink-muted))]">
          <span>See where your store is invisible — and what to do about it</span>
        </div>
        <div className={`${rule} mt-6`} />
      </header>

      {/* Hero — editorial cover */}
      <section className="px-8 md:px-14 pt-10 md:pt-14 pb-14 md:pb-20 max-w-[1400px] mx-auto">
        <h1
          className="font-serif text-[hsl(var(--ink))] leading-[0.98] tracking-[-0.015em]"
          style={{ fontSize: "clamp(2.25rem, 5vw, 5rem)" }}
        >
          Customers are already searching for what you sell.
          <span className="block mt-3 italic" style={{ color: "hsl(var(--plum))" }}>
            The question is…are they finding you?
          </span>
        </h1>
        <div className="mt-10 grid md:grid-cols-12 gap-8 md:gap-10 items-start">
          <div className="md:col-span-7">
            <p className="text-lg md:text-xl leading-[1.65] text-[hsl(var(--ink))] max-w-[54ch]">
              Most independent retailers have no idea where they're easy to find, where they're
              invisible, or what deserves their attention first. Retail Ranker uncovers those
              opportunities, then tells you exactly where to focus.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-5">
              <Link
                to="/preview/first-analysis"
                className="inline-flex items-center gap-3 px-7 py-4 text-[12px] tracking-[0.22em] uppercase hover:opacity-90 transition-opacity"
                style={{ backgroundColor: "hsl(var(--gold))", color: "hsl(var(--gold-ink))" }}
              >
                See what my store is missing
                <span aria-hidden>→</span>
              </Link>
              <Link
                to="/preview/weekly-issue"
                className="text-[12px] tracking-[0.22em] uppercase underline underline-offset-[6px] decoration-[hsl(var(--ink))]/40 hover:decoration-[hsl(var(--ink))]"
              >
              See a Visibility Snapshot
              </Link>
            </div>
            <p className="mt-5 text-xs text-[hsl(var(--ink-muted))]">
              Your first Visibility Snapshot is on the house. No account, no card, no clever tricks.
            </p>
          </div>

          <aside className="md:col-span-5">
            <img
              src={heroImage}
              alt="Warm boutique interior with pottery, textiles, and natural light"
              className="w-full h-auto object-cover rounded-sm"
              loading="eager"
            />
          </aside>
        </div>
      </section>

      <div className="px-8 md:px-14 max-w-[1400px] mx-auto"><div className={rule} /></div>

      {/* Editorial ticker */}
      <div
        className="border-b border-[hsl(var(--ink))]/25 overflow-hidden"
        style={{ backgroundColor: "hsl(var(--plum))", color: "hsl(var(--background))" }}
        aria-hidden
      >
        <style>{`
          @keyframes rr-ticker { from { transform: translateX(0); } to { transform: translateX(-50%); } }
          .rr-ticker-track { animation: rr-ticker 22s linear infinite; }
          .rr-ticker-track:hover { animation-play-state: paused; }
        `}</style>
        <div className="flex whitespace-nowrap rr-ticker-track py-2 text-[10px] tracking-[0.3em] uppercase">
          {[0, 1].map((k) => (
            <div key={k} className="flex shrink-0 items-center">
              {[
                "What are customers looking for?",
                "Are they finding your store?",
                "What are you missing?",
                "What deserves more visibility?",
                "What should you focus on next?",
              ].map((item, i) => (
                <span key={`${k}-${i}`} className="flex items-center">
                  <span className="px-6 opacity-90">{item}</span>
                  <span className="opacity-50" style={{ color: "hsl(var(--gold))" }}>✦</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* § 02 — What we do */}
      <section className="px-8 md:px-14 py-14 md:py-20 max-w-[1400px] mx-auto">
        <p className={kicker}>02 — Why this matters</p>
        <div className="mt-8 grid md:grid-cols-12 gap-8">
          <h2 className="md:col-span-7 font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight">
            Being invisible isn't a marketing problem.{" "}
            <span className="italic" style={{ color: "hsl(var(--plum))" }}>
              It's a business decision you're making without meaning to.
            </span>
          </h2>
          <div className="md:col-span-5 md:pt-2 space-y-5 text-[15px] text-[hsl(var(--ink-muted))] leading-[1.75]">
            <p>
              Every day, customers nearby are searching for things you sell — brands,
              services, gifts, occasions, experiences — and quietly ending up somewhere
              else. You promote the products you assume matter most. You spend on ads
              for the things top-of-mind. You pass over the opportunities you never
              knew were there.
            </p>
            <p>
              Ranking is the point — but not the way most tools mean it. We rank your
              opportunities: what to feature, what to stop advertising, which products
              deserve more attention, and where to focus first. Better direction.
              Fewer wasted moves.
            </p>
          </div>
        </div>
      </section>

      <div className="px-8 md:px-14 max-w-[1400px] mx-auto"><div className={rule} /></div>

      {/* § 03 — How it works */}
      <section className="px-8 md:px-14 py-14 md:py-20 max-w-[1400px] mx-auto">
        <p className={kicker}>03 — The reasoning</p>
        <div className="mt-8 grid md:grid-cols-3 gap-10 md:gap-12">
          {[
            {
              title: "We understand your shop",
              body: "The stock, the brands, the services, the workshops, the events, the sort of customer you serve. What makes your shop the right answer for the people around it.",
              icon: (
                <svg viewBox="0 0 48 48" fill="none" className="w-10 h-10" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M8 20 L8 40 L40 40 L40 20" />
                  <path d="M8 20 L12 12 L36 12 L40 20" />
                  <path d="M6 20 L42 20" />
                  <rect x="12" y="26" width="8" height="10" rx="0.5" />
                  <path d="M16 26 L16 36" />
                  <path d="M12 31 L20 31" />
                  <rect x="28" y="26" width="8" height="10" rx="0.5" />
                  <path d="M32 26 L32 36" />
                  <path d="M28 31 L36 31" />
                  <rect x="20" y="32" width="8" height="8" rx="0.5" />
                </svg>
              ),
            },
            {
              title: "We understand your market",
              body: "What people nearby are actually looking for — products, brands, gifts, seasons, occasions, experiences. Not a keyword list. The real shape of local demand.",
              icon: (
                <svg viewBox="0 0 48 48" fill="none" className="w-10 h-10" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="24" cy="24" r="16" />
                  <circle cx="24" cy="24" r="5" />
                  <path d="M24 6 L27 24 L24 42 L21 24 Z" />
                  <path d="M6 24 L24 21 L42 24 L24 27 Z" />
                  <path d="M24 10 L24 14" />
                  <path d="M24 34 L24 38" />
                  <path d="M10 24 L14 24" />
                  <path d="M34 24 L38 24" />
                </svg>
              ),
            },
            {
              title: "We compare the two",
              body: "Where your shop is invisible to demand that already exists, ranked by what matters most. A short, prioritized list of what to do next — with the reasoning shown, in plain English.",
              icon: (
                <svg viewBox="0 0 48 48" fill="none" className="w-10 h-10" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M8 40 L8 34 L16 34 L16 28 L26 28 L26 20 L36 20 L36 40 Z" />
                  <path d="M8 34 L16 34" />
                  <path d="M16 28 L26 28" />
                  <path d="M26 20 L36 20" />
                  <path d="M12 34 L12 40" />
                  <path d="M21 28 L21 40" />
                  <path d="M31 20 L31 40" />
                </svg>
              ),
            },
          ].map((s) => (
            <article key={s.title}>
              <div className="text-[hsl(var(--plum))] opacity-80">{s.icon}</div>
              <h3 className="font-serif text-3xl mt-6 leading-tight">{s.title}</h3>
              <p className="text-[15px] leading-[1.75] text-[hsl(var(--ink-muted))] mt-4">{s.body}</p>
            </article>
          ))}
        </div>
      </section>

      <div className="px-8 md:px-14 max-w-[1400px] mx-auto"><div className={rule} /></div>

      {/* § 04 — Two ways in */}
      <section className="px-8 md:px-14 py-24 md:py-32 max-w-[1400px] mx-auto">
        <p className={kicker}>04 — Where to begin</p>
        <div className="mt-10 grid md:grid-cols-2 gap-8 md:gap-12">
          <article className="border border-[hsl(var(--ink))]/20 p-10 md:p-12 bg-[hsl(var(--paper))]">
            <p className={kicker}>Free</p>
            <h3 className="font-serif text-4xl leading-tight mt-4">Visibility Snapshot</h3>
            <p className="text-[15px] leading-[1.75] text-[hsl(var(--ink-muted))] mt-6">
              A one-time, honest look at your shop against the demand around it.
              What customers nearby are searching for, where you're quietly missing
              from the answer, and the handful of moves worth making first —
              prioritized by what matters most for a shop like yours.
            </p>
            <ul className="space-y-3 text-[14px] mt-8">
              <li>— What your town is actually shopping for</li>
              <li>— The moments your shop is missing from</li>
              <li>— Your biggest opportunities, prioritized</li>
              <li>— Plain English. No spreadsheets, no jargon.</li>
            </ul>
            <Link
              to="/preview/first-analysis"
              className="inline-block mt-10 text-[12px] tracking-[0.22em] uppercase underline underline-offset-[6px] decoration-[hsl(var(--ink))]/40 hover:decoration-[hsl(var(--ink))]"
            >
              See a Visibility Snapshot →
            </Link>
          </article>

          <article
            className="p-10 md:p-12"
            style={{ backgroundColor: "hsl(var(--plum))", color: "hsl(var(--background))" }}
          >
            <p className="uppercase tracking-[0.28em] text-[11px] opacity-70">Paid</p>
            <h3 className="font-serif text-4xl leading-tight mt-4">
              Complete <span className="italic" style={{ color: "hsl(var(--plum-soft))" }}>Discoverability Analysis</span>
            </h3>
            <p className="text-[15px] leading-[1.75] opacity-80 mt-6">
              Demand doesn't sit still, and neither should your priorities.
              We keep comparing your shop against what your town is looking
              for — surfacing what's newly worth attention, what to stop
              spending on, and what to lean into next.
            </p>
            <ul className="space-y-3 text-[14px] mt-8 opacity-90">
              <li>— Fresh priorities as demand shifts</li>
              <li>— New opportunities you didn't know existed</li>
              <li>— No dashboards. No logins. Just direction.</li>
            </ul>
            <Link
              to="/preview/weekly-issue"
              className="inline-block mt-10 text-[12px] tracking-[0.22em] uppercase underline underline-offset-[6px] opacity-90 hover:opacity-100"
            >
              See how it works →
            </Link>
          </article>
        </div>
      </section>

      {/* Awning divider — atelier storefront */}
      <div className="w-full overflow-hidden leading-[0]">
        <svg viewBox="0 0 1200 220" preserveAspectRatio="none" className="w-full h-[100px] md:h-[150px] block" aria-hidden="true">
          <defs>
            {/* Rich woven stripe — plum + ivory with fabric texture */}
            <pattern id="awning-stripe" width="56" height="220" patternUnits="userSpaceOnUse">
              {/* Base plum */}
              <rect width="28" height="220" fill="hsl(var(--plum))" />
              {/* Ivory band with warm cast */}
              <rect x="28" width="28" height="220" fill="hsl(var(--paper))" />
              {/* Weft texture — horizontal threads */}
              <g opacity="0.06">
                {Array.from({ length: 45 }, (_, i) => (
                  <line key={i} x1="0" y1={i * 5} x2="56" y2={i * 5} stroke="hsl(var(--ink))" strokeWidth="0.6" />
                ))}
              </g>
              {/* Fold shadows */}
              <rect width="7" height="220" fill="hsl(var(--ink))" opacity="0.09" />
              <rect x="28" width="5" height="220" fill="hsl(var(--ink))" opacity="0.05" />
            </pattern>

            {/* Deep body shadow */}
            <filter id="awning-shadow" x="-2%" y="0" width="104%" height="150%">
              <feDropShadow dx="0" dy="20" stdDeviation="18" floodColor="hsl(var(--ink))" floodOpacity="0.14" />
            </filter>

            {/* Scallop rolled-edge shadow */}
            <linearGradient id="scallop-shade" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="hsl(var(--ink))" stopOpacity="0.22" />
              <stop offset="30%" stopColor="hsl(var(--ink))" stopOpacity="0" />
              <stop offset="70%" stopColor="hsl(var(--ink))" stopOpacity="0" />
              <stop offset="100%" stopColor="hsl(var(--ink))" stopOpacity="0.18" />
            </linearGradient>

            {/* Brass finial */}
            <g id="finial">
              <circle cx="0" cy="0" r="5" fill="hsl(var(--gold))" opacity="0.9" />
              <circle cx="0" cy="0" r="2.5" fill="hsl(var(--paper))" opacity="0.35" />
            </g>
          </defs>

          {/* Main awning — fewer, deeper scallops; pronounced belly */}
          <path
            d="M0,32 Q600,42 1200,32 L1200,128
               Q1140,182 1080,128 Q1020,182 960,128
               Q900,182 840,128 Q780,182 720,128
               Q660,182 600,128 Q540,182 480,128
               Q420,182 360,128 Q300,182 240,128
               Q180,182 120,128 Q60,182 0,128 Z"
            fill="url(#awning-stripe)"
            filter="url(#awning-shadow)"
          />

          {/* Scallop under-shadow */}
          <path
            d="M0,128
               Q60,182 120,128 Q180,182 240,128
               Q300,182 360,128 Q420,182 480,128
               Q540,182 600,128 Q660,182 720,128
               Q780,182 840,128 Q900,182 960,128
               Q1020,182 1080,128 Q1140,182 1200,128
               L1200,145 L0,145 Z"
            fill="url(#scallop-shade)"
          />

          {/* Moulded header cornice */}
          <rect x="0" y="0" width="1200" height="32" fill="hsl(var(--ink))" opacity="0.90" />
          {/* Top highlight bevel */}
          <rect x="0" y="0" width="1200" height="2" fill="hsl(var(--paper))" opacity="0.20" />
          {/* Mid moulding step */}
          <rect x="0" y="18" width="1200" height="6" fill="hsl(var(--ink))" opacity="0.40" />
          {/* Gold band */}
          <rect x="0" y="28" width="1200" height="3" fill="hsl(var(--gold))" opacity="0.80" />
          {/* Bottom edge shadow */}
          <rect x="0" y="30" width="1200" height="2" fill="hsl(var(--ink))" opacity="0.50" />
        </svg>
      </div>

      {/* Closing */}
      <section className="px-8 md:px-14 pt-10 pb-28 md:pb-40 max-w-[1400px] mx-auto text-center">
        <h2 className="font-serif leading-[1.05]" style={{ fontSize: "clamp(2.5rem, 6vw, 5rem)" }}>
          Enough guessing. Enough gut feel.<br />
          Enough marketing the wrong things.<br />
          <span className="italic" style={{ color: "hsl(var(--plum))" }}>See what your store is missing.</span>
        </h2>
        <Link
          to="/preview/first-analysis"
          className="inline-flex items-center gap-3 mt-12 px-8 py-4 text-[12px] tracking-[0.22em] uppercase hover:opacity-90 transition-opacity"
          style={{ backgroundColor: "hsl(var(--gold))", color: "hsl(var(--gold-ink))" }}
        >
          See what I'm missing — free <span aria-hidden>→</span>
        </Link>
      </section>

      {/* Footer strip */}
      <footer className="px-8 md:px-14 py-8 border-t border-[hsl(var(--ink))]/25 text-[10px] tracking-[0.3em] uppercase text-[hsl(var(--ink-muted))] flex flex-col md:flex-row items-center justify-between gap-3">
        <span>Retail Ranker · Retail Discoverability Intelligence</span>
        <span>© {year} · For independent shops, everywhere</span>
        <div className="flex gap-8">
          <Link to="/faq" className="hover:text-[hsl(var(--ink))]">FAQ</Link>
          <Link to="/contact" className="hover:text-[hsl(var(--ink))]">Contact</Link>
        </div>
      </footer>
    </div>
  );
};

export default Index;
